// SimDriver — the SimUIForm lockstep slice: block pacing, per-block hash reporting, and the
// BeginTrackState/EndTrackState frame orchestration. game/SimUI.cpp:313-315 (init handshake),
// :586-611 (QueueUpdateMessage), :659-744 (ProcessUpdateMessage), :745-757
// (SendUpdateResult -> the onUpdateResult seam), :814-824 (the per-tick step), :848-880
// (Begin/EndTrackState).
//
// THE CONTRACT (m_cUpdatesBlock = the NEXT block point):
//   updates+1 > block  -> broken (no update)
//   updates+1 < block  -> run an EMPTY update (commands clear)
//   updates+1 == block -> need a queued UpdateNetMessage (else BLOCK); dequeue it, enqueue
//     its commands, hash = tracker.GetHash() REPORTED FOR THE OLD BLOCK (skipped when the
//     old block is 0 — the bootstrap), tracker.SetNextBlock(), block = msg.cUpdatesBlock,
//     then run the update that executes the commands.
// Bootstrap: Simulation starts at m_cUpdates == -1 and the driver at block 0, so the very
// first message is consumed report-free and update 0 is the first tracked update.

import type { SimContext } from '../context.ts';
import { quad } from '../hash/quads.ts';
import { CommandQueue } from '../sm/commandqueue.ts';
import type { Message } from '../sm/message.ts';
import type { Simulation } from './simulation.ts';

/** The decoded UpdateNetMessage (ScUpdate / GAMEUPDATEEMPTY): the next block point, the
 *  server's sync point (frame expiry), and the verbatim command list. */
export interface UpdateNetMsg {
  cUpdatesBlock: number;
  cUpdatesSync: number;
  commands: Message[];
}

export class SimDriver {
  private ctx: SimContext;
  private sim: Simulation;
  private m_pcmdqServer = new CommandQueue();
  private m_punmr: UpdateNetMsg[] = [];
  m_cUpdatesBlock = 0; // SimUI.cpp:313

  /** SendUpdateResult seam (SimUI.cpp:745) — the re-sim oracle hook. The C++ also sends a
   *  (0,0,0) liveness handshake at init (SimUI.cpp:315); hashing ignores it. */
  onUpdateResult: (cUpdatesBlock: number, hash: number) => void = () => {};

  /** Per-update frame seam (the WI_AUTOMATION EndTrackState capture hook,
   *  SimUI.cpp:859-880): fires with the finished frame's ToJson after every update.
   *  null (the default) skips the ToJson build entirely — it is pure overhead
   *  unless an embedder consumes frames. */
  onFrame: ((cUpdates: number, frameJson: string) => void) | null = null;

  constructor(ctx: SimContext, sim: Simulation) {
    this.ctx = ctx;
    this.sim = sim;
    this.m_pcmdqServer.Init();
  }

  // game/SimUI.cpp:586-592 — expire acked frames on receipt, queue at the tail
  QueueUpdateMessage(punm: UpdateNetMsg): void {
    this.ctx.tracker.ExpireFrames(punm.cUpdatesSync);
    this.m_punmr.push(punm);
  }

  /** Queued-but-unconsumed UpdateNetMsgs — the m_punmrFirst list walks of the
   *  live client (SimUI.cpp:610-621 fUpdateNow, :638-661 RunUpdatesNow). */
  get cQueued(): number {
    return this.m_punmr.length;
  }

  /** Set by Sim.load: the first dequeued block after a resume cannot be hashed (the
   *  pre-save frame window is not in the save format), so its report is suppressed —
   *  reporting GetHash() over the empty tracker would emit a false 0. */
  m_fSuppressNextReport = false;

  /** When false, skip the per-update StateFrame tracking + the per-block hash report
   *  entirely (Sim.create/restore `trackState: false`). The hash is the MULTIPLAYER
   *  desync comparand — it is READ-ONLY on sim state, so disabling it leaves the
   *  simulation bit-identical (proven: identical snapshot bytes) while removing ~19% of
   *  the per-step cost. Use it for read-only re-sims that never compare hashes — e.g. the
   *  replay viewer's background checkpoint prewarm. Default true: MP + every existing
   *  caller keep hashing. (With tracking off, onFrame x-rays are unavailable too.) */
  m_fTrackState = true;

  // game/SimUI.cpp:659-744
  private ProcessUpdateMessage(): boolean {
    if (this.sim.GetUpdateCount() + 1 > this.m_cUpdatesBlock) {
      // The C++ Trace("Broken!")s and stalls; for the embedding API a silent permanent
      // freeze (every later message pinned forever) is an unacceptable failure mode —
      // this state is unreachable from a valid recording, so it is an embedder bug.
      throw new Error(
        `SimDriver: block point regressed — sim at update ${this.sim.GetUpdateCount()} ` +
          `but the current block point is ${this.m_cUpdatesBlock} (duplicate or out-of-order block?)`,
      );
    }

    if (this.sim.GetUpdateCount() + 1 < this.m_cUpdatesBlock) {
      this.m_pcmdqServer.Clear();
      return true; // continue the simulation between block points
    }

    if (this.m_punmr.length === 0) {
      return false; // BLOCK until the server's next update message
    }

    const punm = this.m_punmr.shift()!;
    this.m_pcmdqServer.Clear();
    for (const c of punm.commands) this.m_pcmdqServer.Enqueue(c);

    // The per-block report ADVANCES regardless of tracking — lastReportedBlock is resume
    // bookkeeping (the cUpdatesSync ack / snapshot header), not hashing, so a trackState:false
    // snapshot stays byte-identical to a tracked one. GetHash() folds an EMPTY frame ring to 0
    // in O(0) when tracking is off; the consumer onBlockHash is gated in Sim (no bogus 0).
    const hash = this.ctx.tracker.GetHash();
    this.ctx.tracker.SetNextBlock();

    if (this.m_cUpdatesBlock !== 0) {
      if (this.m_fSuppressNextReport) this.m_fSuppressNextReport = false;
      else this.onUpdateResult(this.m_cUpdatesBlock, hash);
    }

    this.m_cUpdatesBlock = punm.cUpdatesBlock;
    return true;
  }

  // game/SimUI.cpp:848-857 — frame for the update ABOUT to run; the SMUI{BLCK} group first
  private BeginTrackState(): void {
    if (!this.m_fTrackState) return; // tracking disabled → no per-update frame
    const cUpdates = this.sim.GetUpdateCount() + 1;
    const frame = this.ctx.tracker.AddFrame(cUpdates);
    const i = frame.AddCountedValue(quad('SMUI'));
    frame.AddValue(quad('BLCK'), this.m_cUpdatesBlock, i);
    this.ctx.frameCurrent = frame;
  }

  // game/SimUI.cpp:859-880 — TrackState then the WI_AUTOMATION-style frame capture
  private EndTrackState(): void {
    if (!this.m_fTrackState) return; // tracking disabled → nothing to fold/capture
    const frame = this.ctx.frameCurrent!;
    this.sim.TrackState(frame);
    this.ctx.frameCurrent = null;
    if (this.onFrame !== null) this.onFrame(this.sim.GetUpdateCount(), frame.ToJson());
  }

  /** One lockstep step (the kfRoleMultiplayer arm of SimUI.cpp:814-824). False = blocked.
   *  MP: ctx.cmdq (gcmdq) is flushed-to-server-and-cleared each UI frame
   *  (SimUI.cpp:817-834); the relayed copies come back inside recorded blocks, so a
   *  re-sim must Clear without consuming, and the update runs on the SERVER queue.
   *  SP (SimUI.cpp:846-850): NO flush — gsim.Update(&gcmdq) consumes the LOCAL queue
   *  directly (locally-generated commands — trigger actions, unit groups — execute at
   *  the next update; pending ones survive in gcmdq across pauses/saves). */
  StepOnce(): boolean {
    if (this.ctx.fMultiplayer) this.ctx.cmdq.Clear();
    if (!this.ProcessUpdateMessage()) return false;
    this.BeginTrackState();
    this.sim.Update(this.ctx.fMultiplayer ? this.m_pcmdqServer : this.ctx.cmdq);
    this.EndTrackState();
    // ggame.UpdateTriggers() — the trigger pump fires BETWEEN updates (SimUI.cpp:645/655
    // RunUpdatesNow + game.cpp:2270 FilterEvent), i.e. after this update's TrackState.
    if (!this.fSuppressTriggerPump) this.sim.RunPendingTriggerUpdate();
    return true;
  }

  /** Model the SP post-load window of the real client: a trigger pump scheduled
   *  during the update(s) stepped right after a load is deterministically KILLED
   *  before it services — Game::GameLoop entry (game.cpp `m_fUpdateTriggers =
   *  false;`, on loading-screen dismissal) runs after them. The pixel oracle sets
   *  this for its stepped window so both sims freeze at "update done, pump dead". */
  fSuppressTriggerPump = false;

  /** Run until blocked (RunUpdatesNow-style catch-up). Returns updates run. */
  RunQueued(): number {
    let n = 0;
    while (this.StepOnce()) n++;
    return n;
  }
}
